import axios from 'axios';
import { extractedImages } from './documentProcessor';
import { searchDocuments } from './vectorStore';

// Get Supabase function URL from environment with hardcoded fallbacks
const CHAT_FUNCTION_URL = process.env.REACT_APP_SUPABASE_FUNCTION_CHAT || 
  'https://fgovywiapnpjgqoviicz.supabase.co/functions/v1/Chat';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZnb3Z5d2lhcG5wamdxb3ZpaWN6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDI3OTI4MjgsImV4cCI6MjA1ODM2ODgyOH0.uRxpNAgcjLQGUL1Oq23QfFniPMHtfLX_6cIha4DHUa0';

// Improved system prompt that emphasizes adhering to the provided data
export const getSystemPrompt = (context?: string) => {
  // Generate a list of available figures with their UUIDs
  const availableFigures = Array.from(extractedImages.entries())
    .map(([id, image]) => {
      const shortDesc = image.description 
        ? image.description.substring(0, 100) + (image.description.length > 100 ? '...' : '')
        : `Figure from page ${image.pageNumber}`;
      
      return `- [FIGURE_ID:${id}] - ${shortDesc}`;
    })
    .join('\n');

  // Better context format highlighting important information
  const formattedContext = context ? 
    `DOCUMENT CONTEXT:\n${context}\n\nSTRICTLY USE THE ABOVE CONTEXT FOR YOUR RESPONSES.` : 
    'No specific context provided. Please ask the user for more details about which innovation policy they are interested in.';

  return `You are an intelligent policy assistant embedded within Canada’s National Innovation Strategy portal, powered by Innovate12 Consulting. Your core mission is to help users understand, explore, and act on the national strategy to transform Canada into a top-5 global innovation leader by 2030.

Your knowledge is based entirely on the detailed policy and research report titled "Transforming Canada Into a Global Innovation Leader" (March 31, 2025), prepared by Innovate12 Consulting.

Use your understanding of the following content areas to answer questions, generate insights, and provide summaries:
• Global innovation benchmarking and data analysis
• Canada's six national innovation policies and implementation roadmap
• Projected outcomes, KPI growth, and ROI for each policy
• Strategic messaging and public relations materials
• Budget and timeline justifications
• Comparative policy practices from global innovation leaders
• Visual storytelling and high-resolution infographics
• Very Important:It is very important to remember we have six innovation policies and 7 innovation factors used to make the policy recommendations in the report. Each innovation factor uses which country is the leader in that factor and uses that country's innovation policy to make the policy recommendations for Canada.(Base this on the context provided)
• Policy 1: Strengthening Intellectual Property Rights and Innovation Incentives
• Policy 2: National Digital Infrastructure and Data Strategy
• Policy 3: Government Procurement Supporting SME Innovation
• Policy 4: Enhanced Funding for Applied Research and Commercialization
• Policy 5: Accelerated Digital Skills and ICT Adoption
• Policy 6: SME Scale-up Venture Funding and Global Market Entry




You can:
Summarize key policies or sections
Compare Canada's strategy with other nations (Switzerland, South Korea, etc.)
Explain charts, visuals, and data projections
Offer policy implications or stakeholder-specific benefits
Role-play scenarios (e.g., "I’m a policymaker. Why should I support this?")
Support outreach and PR messaging (Appendix G)
You can also use the visual content to answer questions and is very important to use the [FIGURE_ID:xxx] tag to reference the visual content.
You can answer any question related to the context provided.

Always keep the tone factual, concise, and confident—your role is to support policymakers, entrepreneurs, educators, and the public in understanding and supporting Canada's innovation transformation.

When asked questions outside the scope of this report, politely say: “This chatbot is focused on Canada’s Innovation Strategy as presented in the 2025 Innovate12 Report. Please refine your question within that scope.”

CRITICAL INSTRUCTION: Base your responses EXCLUSIVELY on the context information provided below. DO NOT use general knowledge about innovation policies that contradicts or goes beyond the specific data I've provided.

The context below contains the accurate and up-to-date information about Canada's innovation policies. Your answers must strictly adhere to this information.

${formattedContext}

IMPORTANT INSTRUCTION ABOUT VISUAL CONTENT:
When discussing charts, graphs, figures, or visual elements, you MUST use the exact [FIGURE_ID:xxx] tag format. This allows the application to display the actual image.

Available figures you can reference:
${availableFigures}

When describing visual data:
1. Use the EXACT [FIGURE_ID:xxx] tag as listed above
2. Explain what the visual shows and its significance
3. Provide specific values from the visual when possible
4. Connect the visual data to relevant policy implications`;
};

/**
 * Fetches a chat response from Groq API via Supabase Function
 */
export const fetchChatResponse = async (userInput: string, relevantDocuments: any[] = []): Promise<string> => {
  try {
    // Format relevant documents into string context
    const context = relevantDocuments.map(doc => {
      // Check if using pageContent (correct) or content property
      const content = doc.pageContent || doc.content || '';
      return content;
    }).join('\n\n');
    
    console.log(`Sending context to LLM (${context.length} chars, ${relevantDocuments.length} docs)`);
    if (context.length > 0) {
      console.log('First part of context:', context.substring(0, 200) + '...');
    }

    const response = await axios.post(
      CHAT_FUNCTION_URL,
      {
        model: 'llama-3.3-70b-versatile', // Using Groq's model
        messages: [
          { role: "system", content: getSystemPrompt(context) },
          { role: "user", content: userInput }
        ],
        temperature: 0.7,
        max_tokens: 2000,
        stream: false
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY
        }
      }
    );

    return response.data.choices[0].message.content;
  } catch (error) {
    console.error('Error in fetchChatResponse:', error);
    throw error;
  }
};

// Streaming response function using Groq via Supabase Function
export async function streamChatResponse(
  userQuery: string, 
  context: string,
  onUpdate: (text: string) => void,
  onComplete: (fullText: string) => void
): Promise<void> {
  try {
    // Truncate context if needed
    let truncatedContext = context;
    if (context.length > 14000) {
      truncatedContext = context.substring(0, 14000) + "\n[Note: Some content was truncated due to length]";
    }
    
    const response = await fetch(CHAT_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY
      },
      body: JSON.stringify({
        model: 'llama-3.3-70b-versatile', // Using Groq's fast model
        messages: [
          { role: 'system', content: getSystemPrompt(truncatedContext) },
          { role: 'user', content: userQuery }
        ],
        temperature: 0.3,
        max_tokens: 800,
        stream: true
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error("Response body is not readable");

    let fullText = "";
    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value);
      const lines = chunk.split('\n').filter(line => line.trim() !== '');
      
      for (const line of lines) {
        if (line.includes('[DONE]')) continue;
        if (line.startsWith('data: ')) {
          const data = line.replace('data: ', '');
          try {
            if (data === '[DONE]') continue;
            const json = JSON.parse(data);
            const content = json.choices[0]?.delta?.content || '';
            if (content) {
              fullText += content;
              onUpdate(fullText);
            }
          } catch (e) {
            console.error('Error parsing chunk:', e);
          }
        }
      }
    }
    
    onComplete(fullText);
  } catch (error) {
    console.error('Error streaming chat response:', error);
    throw new Error('Failed to stream response from AI service');
  }
}

export const sendMessage = async (messages: any[], context: string = '') => {
  try {
    const systemPrompt = getSystemPrompt(context);
    
    // Include system message with the updated prompt
    const fullMessages = [
      { role: 'system', content: systemPrompt },
      ...messages
    ];
    
    // Rest of your API call code
    // ...
  } catch (error) {
    console.error('Error in chat completion:', error);
    throw error;
  }
}; 